import maplibregl, {
  AddLayerObject,
  GeoJSONSource,
  LayerSpecification,
} from 'maplibre-gl';

import Logger from '../logger';

const TIME_LIMIT = 5000;
const TIME_INCREMENT = 500;

export async function isLayerLoaded(
  map: maplibregl.Map,
  layerId: string
): Promise<boolean> {
  return new Promise((resolve) => {
    let counter = 0;

    const interval = setInterval(() => {
      if (counter > TIME_LIMIT) {
        resolve(false);
        clearInterval(interval);
      }

      counter += TIME_INCREMENT;
      try {
        if (map.getLayer(layerId)) {
          clearInterval(interval);

          resolve(true);
        }
      } catch (err) {
        Logger.error(err);
        resolve(false);
      }
    }, TIME_INCREMENT);
  });
}

export async function getQueriedRenderedFeatures(
  map: maplibregl.Map,
  layerIds: string[]
): Promise<maplibregl.MapGeoJSONFeature[]> {
  const timeInterval = 1;
  const timeout = 100;
  return new Promise((resolve) => {
    let counter = 0;

    const interval = setInterval(() => {
      if (counter > timeout) {
        resolve([]);
        clearInterval(interval);
      }

      counter += timeInterval;

      const features = map.queryRenderedFeatures(undefined, {
        layers: layerIds,
      });

      if (features.length) {
        clearInterval(interval);

        resolve(features);
      }
    }, timeInterval);
  });
}

export async function layerHasQueryableSource(
  map: maplibregl.Map,
  layerId: string,
  sourceLayer: string
): Promise<boolean> {
  const timeInterval = 10;
  const timeout = 100;
  return new Promise((resolve) => {
    let counter = 0;

    const interval = setInterval(() => {
      if (counter > timeout) {
        resolve(false);
        clearInterval(interval);
      }

      counter += timeInterval;

      const features = map.querySourceFeatures(layerId, {
        sourceLayer,
      });
      if (features.length > 0) {
        resolve(true);
        clearInterval(interval);
      }
    }, timeInterval);
  });
}

export async function isTileLoading(map: maplibregl.Map): Promise<boolean> {
  return new Promise((resolve) => {
    let counter = 0;

    const interval = setInterval(() => {
      if (counter > TIME_LIMIT) {
        resolve(false);
        clearInterval(interval);
      }

      counter += TIME_INCREMENT;

      try {
        if (!map.areTilesLoaded()) {
          clearInterval(interval);
          resolve(true);
        }
      } catch (err) {
        Logger.error(err);
        resolve(false);
      }
    }, TIME_INCREMENT);
  });
}

export async function getGeoJSONSource(
  map: maplibregl.Map,
  sourceId: string
): Promise<GeoJSONSource | undefined> {
  return new Promise((resolve) => {
    let counter = 0;

    const interval = setInterval(() => {
      if (counter > TIME_LIMIT) {
        resolve(undefined);
        clearInterval(interval);
      }

      counter += TIME_INCREMENT;

      try {
        if (map.getSource(sourceId)) {
          clearInterval(interval);
          resolve(map.getSource(sourceId) as GeoJSONSource);
        }
      } catch (error) {
        resolve(undefined);
      }
    });
  });
}

export async function hasControl(
  map: maplibregl.Map,
  control?: maplibregl.IControl
): Promise<boolean | undefined> {
  return new Promise((resolve) => {
    let counter = 0;

    const interval = setInterval(() => {
      if (counter > TIME_LIMIT) {
        resolve(undefined);
        clearInterval(interval);
      }

      counter += TIME_INCREMENT;

      try {
        if (control) {
          if (map.hasControl(control)) {
            clearInterval(interval);
            resolve(map.hasControl(control));
          }
        } else {
          resolve(false);
        }
      } catch (error) {
        resolve(undefined);
      }
    });
  });
}

export async function addSource(
  map: maplibregl.Map,
  id: string,
  source: maplibregl.SourceSpecification | maplibregl.CanvasSourceSpecification
): Promise<GeoJSONSource | undefined> {
  return new Promise((resolve) => {
    let counter = 0;

    if (map.getSource(id)) {
      map.removeSource(id);
    }

    const interval = setInterval(() => {
      if (counter > TIME_LIMIT) {
        resolve(undefined);
        clearInterval(interval);
      }

      counter += TIME_INCREMENT;

      try {
        map.addSource(id, source);
        if (map.getSource(id)) {
          clearInterval(interval);
          resolve(map.getSource(id) as GeoJSONSource);
        }
      } catch (error) {
        resolve(undefined);
      }
    });
  });
}

export async function removeSource(
  map: maplibregl.Map,
  id: string
): Promise<boolean | undefined> {
  return new Promise((resolve) => {
    let counter = 0;

    if (map.getSource(id)) {
      map.removeSource(id);
    }

    const interval = setInterval(() => {
      if (counter > TIME_LIMIT) {
        resolve(undefined);
        clearInterval(interval);
      }

      counter += TIME_INCREMENT;

      try {
        if (map.getSource(id)) {
          clearInterval(interval);
          map.removeSource(id);
        }
      } catch (error) {
        resolve(undefined);
      }
    });
  });
}

export async function addControl(
  map: maplibregl.Map,
  control: maplibregl.IControl
): Promise<boolean | undefined> {
  return new Promise((resolve) => {
    let counter = 0;

    const interval = setInterval(() => {
      if (counter > TIME_LIMIT) {
        resolve(undefined);
        clearInterval(interval);
      }

      counter += TIME_INCREMENT;

      try {
        if (map.hasControl(control)) {
          clearInterval(interval);
          resolve(true);
        } else {
          map.addControl(control);
        }
      } catch (error) {
        resolve(undefined);
      }
    });
  });
}

export async function addLayer(
  map: maplibregl.Map,
  layer: LayerSpecification | AddLayerObject
): Promise<LayerSpecification | undefined> {
  return new Promise((resolve) => {
    let counter = 0;

    const interval = setInterval(() => {
      if (counter > TIME_LIMIT) {
        resolve(undefined);
        clearInterval(interval);
      }

      counter += TIME_INCREMENT;

      try {
        if (
          'source' in layer &&
          typeof layer.source === 'string' &&
          !map.getSource(layer.source)
        ) {
          resolve(undefined);
          return;
        }

        map.addLayer(layer);
        if (map.getLayer(layer.id)) {
          clearInterval(interval);
          resolve(map.getLayer(layer.id) as any);
        }
      } catch (error) {
        resolve(undefined);
      }
    });
  });
}
